<template>
  <v-container fluid>
    <v-dialog v-model="dialogCrew.show" persistent :overlay="false" max-width="600px">
      <v-card>
        <v-form ref="form" lazy-validation>
          <v-card-title class="title primary white--text mb-4 text-capitalize">{{dialogCrew.crew && dialogCrew.crew.id ? 'Edit' : 'Add'}} Crew</v-card-title>
          <v-card-text>
            <p>
              Please provide the appropriate details for this crew.
            </p>
            <job-selector
              @jobSiteChange="dialogCrew.crew.selectedJobSite = $event;"
              :preselectJobSiteId="dialogCrew.crew.jobSite"
              :noAsbestosFilter="true"
              showHint="Leave blank to utilize crew for all job sites."
              class="mb-5"
            />
            <v-text-field
              label="Crew Name"
              v-model="dialogCrew.crew.name"
              type="text"
              color="primary"
              filled
            >
            </v-text-field>
            <v-autocomplete
              placeholder="Employee Search"
              v-model="dialogCrew.crew.employees"
              :items="allEmployees"
              item-value="docId"
              item-text="name"
              label="Employees"
              multiple
              filled
              hide-details
            ></v-autocomplete
            >
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              text
              @click="cancelDialogCrew"
              >Cancel</v-btn
            >
            <v-btn
              color="primary"
              @click="addCrew()"
              :loading="dialogCrew.saving"
              :disabled="dialogCrew.saving"
              >
              Save
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
    <h1 class="headline mb-4 pt-4">
      <v-btn
        x-small
        text
        style="margin-top: -3px"
        class="mr-2"
        :to="`/employee-schedule`"
        ><v-icon>chevron_left</v-icon>
      </v-btn>
      Employee Schedule Crews
    </h1>
    <v-row v-if="loading">
      <v-col cols="12" sm="12">
        <v-skeleton-loader type="card"></v-skeleton-loader>
      </v-col>
    </v-row>
    <v-row v-if="!loading">
      <v-col cols="12" sm="12">
        <v-card elevation="2" class="mb-4">
          <v-card-title class="text-uppercase">
            Crews
            <v-btn
              color="primary"
              small
              class="ml-4"
              @click="dialogCrew.show = true;"
              >Add Crew</v-btn
            >
          </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="headers"
              :items="crews"
              :search="search"
            >
            <template v-slot:top>
              <v-text-field
                v-model="search"
                outlined
                label="Filter by crew or job site or employee."
                dense
                class="mx-4 pt-4 mb-2"
                hide-details
              ></v-text-field>
            </template>
            <template v-slot:item.jobSiteName="{ item }">
              {{item.jobSiteName ? item.jobSiteName : 'All Job Sites'}}
            </template>
            <template v-slot:item.employeeNames="{ item }">
              <ul>
                <li v-for="item in item.employeeNames">
                  {{ item }}
                </li>
              </ul>
            </template>
            <template v-slot:item.createDate="{ item }">
              {{ item.createDate.toDate() | moment("M/D/YY") }}
            </template>
            <template v-slot:item.options="{ item }">
              <v-btn
                outlined
                text
                small
                color="info"
                :disabled="saving"
                :loading="saving"
                @click="editCrew(item)"
                class="mr-2"
                >
                Edit
              </v-btn>
              <v-btn
                outlined
                text
                small
                color="error"
                :disabled="saving"
                :loading="saving"
                @click="deleteCrew(item)"
                >
                Delete
              </v-btn>
            </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
      {{ snackText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snack = false"> Close </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import fb from "@/main";
import { mapState } from "vuex";
import { sortBy, cloneDeep } from "lodash";
import moment from "moment";
import JobSelector from "@/components/JobSelector";

export default {
  components: {
    JobSelector,
  },
  name: "employee-schedule-crews",
  data() {
    return {
      dates: [],
      loading: true,
      saving: false,
      search: "",
      snack: false,
      snackColor: "",
      snackText: "",
      dialogCrew: {
        saving: false,
        show: false,
        selectedJobSite: null,
        crew: {}
      },
      crews: [],
      headers: [
        { 
          text: "Crew Name",
          value: "name"
        },
        { 
          text: "Job Site",
          value: "jobSiteName"
        },
        { 
          text: "Employees",
          value: "employeeNames"
        },
        {
          text: "Created By",
          value: "createdByName"
        },
        {
          text: "Created",
          value: "createDate"
        },
        {
          text: "",
          value: "options",
          align: "right",
        }
      ],
    };
  },
  watch: {
  },
  async created() {
    if (!this.employees.length) {
      await this.$store.dispatch("getEmployees", fb);
    }
    await this.getCrews();
  },
  computed: {
    allEmployees () {
      return this.employees.map((e) => {
        return {
          name: `${e.firstName} ${e.lastName} (${e.barcodeId})`,
          ...e,
        };
      });
    },
    ...mapState(["user", "employees", "jobSites"])
  },
  methods: {
    async getCrews() {
      this.loading = true;
      const getEmployeeScheduleCrews = fb.db.collection("employeeScheduleCrews");
      const querySnapshot = await getEmployeeScheduleCrews.get();
      const data = querySnapshot.docs.map(doc => {return {id: doc.id, ...doc.data()}});
      data.forEach((d) => {
        d.jobSiteName = (this.jobSites.find((j) => {return d.jobSite === j.id;}) || {}).street || "";
        const employeeNames = d.employees.map((empId) => {
          return ((this.allEmployees.find((a) => {return a.docId === empId;}) || {}).name || "").trim();
        }).sort();
        d.employeeNames = employeeNames;
        d.createdByName = `${d.createdBy.firstName} ${d.createdBy.lastName}`;
      });
      this.crews = sortBy(data, ["jobSite", "name"]);
      this.loading = false;
    },
    async addCrew() {
      if (!this.dialogCrew.crew.employees || !this.dialogCrew.crew.employees.length) {
        alert("Please select employees.")
        return;
      }
      this.dialogCrew.saving = true;
      if (this.dialogCrew.crew.id) {
        const obj = {};
        ["name", "employees"].forEach((key) => {
          obj[key] = this.dialogCrew.crew[key];
        });
        obj.jobSite = this.dialogCrew.crew.selectedJobSite ? this.dialogCrew.crew.selectedJobSite.id : '';
        obj.lastUpdated = new Date();
        obj.lastUpdatedBy = this.user;
        await fb.db.collection("employeeScheduleCrews").doc(this.dialogCrew.crew.id).update(obj);
      } else {
        await fb.db.collection("employeeScheduleCrews").add({
          name: this.dialogCrew.crew.name,
          employees: this.dialogCrew.crew.employees,
          createDate: new Date(),
          createdBy: this.user,
          jobSite: this.dialogCrew.crew.selectedJobSite ? this.dialogCrew.crew.selectedJobSite.id : ''
        });
      }
      this.getCrews();
      this.dialogCrew.crew = {};
      this.dialogCrew.show = false;
      this.dialogCrew.saving = false;
    },
    async editCrew(item) {
      this.dialogCrew.crew = cloneDeep(item);
      this.dialogCrew.show = true;
    },
    async deleteCrew(item) {
      if (confirm("Are you sure?") == true) {
        this.saving = true;
        await fb.db.collection("employeeScheduleCrews").doc(item.id).delete();
        this.getCrews();
        this.saving = false;
        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Successfully deleted crew.";
      }
    },
    cancelDialogCrew() {
      this.dialogCrew.show = false;
      this.dialogCrew.crew = {};
    },
  },
};
</script>
